// react
import { useMemo, useState, useCallback } from 'react';
import styled, { css } from 'styled-components';

// mui
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
} from '@mui/material';

interface VersionProps {
  className?: string;
  nodeEnvironment: string;
  buildEnvironment?: string;
  buildVersion?: string;
  buildHash?: string;
  platform?: string;
  onSignOut: () => void;
  onTestErrorToast?: () => void;
  onTestNotification?: (params: {
    delay: number;
    title: string;
    body: string;
    deepLink: string;
  }) => void;
}

/**
 * Version is a component that renders the version of the app.
 */
export const DevTools = ({
  className,
  nodeEnvironment,
  buildEnvironment,
  buildVersion,
  buildHash,
  platform,
  onSignOut,
  onTestErrorToast,
  onTestNotification,
}: VersionProps) => {
  const [showNotifModal, setShowNotifModal] = useState(false);
  const [notifParams, setNotifParams] = useState({
    delay: 0,
    title: 'Medication reminder',
    body: "It's time to take the morning medication.",
    deepLink: '/organiser',
  });

  const handleSendNotification = useCallback(() => {
    onTestNotification?.(notifParams);
    setShowNotifModal(false);
  }, [onTestNotification, notifParams]);

  const buildEnvironmentComputed = useMemo(() => {
    if (nodeEnvironment === 'development') return 'Local';
    switch (buildEnvironment) {
      case 'local':
        return 'Local';
      case 'dev':
        return 'Development';
      case 'staging':
        return 'Staging';
      default:
        return undefined;
    }
  }, [nodeEnvironment, buildEnvironment]);

  const buildVersionComputed = useMemo(() => {
    if (nodeEnvironment === 'development') return;
    switch (buildEnvironment) {
      case 'dev':
        // regex for eng-### any amount of digits
        const regex = /eng-\d+/;
        // get the eng-### from the build version
        const match = buildVersion?.match(regex);
        // if there is a match, return the match
        if (match) {
          return (
            <LinearLink
              href={`https://linear.app/karehero/issue/${match[0].toUpperCase()}`}
              target='_blank'
              rel='noreferrer'
            >
              {match[0].toUpperCase()}
            </LinearLink>
          );
        }
        return buildVersion;
      case 'staging':
        return `Build ${buildVersion}`;
      default:
        return buildVersion;
    }
  }, [nodeEnvironment, buildEnvironment, buildVersion]);

  if (buildEnvironment === 'production') return null;
  return (
    <Wrapper className={className} $environment={buildEnvironmentComputed}>
      <strong>{buildEnvironmentComputed}</strong>
      {buildVersionComputed && (
        <>
          {' -'} {buildVersionComputed}
        </>
      )}
      {platform && (
        <>
          {' -'} {platform}
        </>
      )}
      <SignOutButton onClick={onSignOut}>Force sign out</SignOutButton>
      {onTestErrorToast && (
        <TestToastButton onClick={onTestErrorToast}>
          Test error toast
        </TestToastButton>
      )}
      {onTestNotification && (
        <TestNotificationButton onClick={() => setShowNotifModal(true)}>
          Test notification
        </TestNotificationButton>
      )}
      <Dialog
        open={showNotifModal}
        onClose={() => setShowNotifModal(false)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Send Test Notification</DialogTitle>
        <DialogContent>
          <Typography
            variant='body2'
            sx={{
              color: 'text.secondary',
              mb: 2,
            }}
          >
            Creates a test notification for your account. A random number is
            appended to the title. Non-production environments only.
          </Typography>
          <Stack spacing={2}>
            <TextField
              label='Title'
              fullWidth
              size='small'
              value={notifParams.title}
              onChange={(e) =>
                setNotifParams((p) => ({ ...p, title: e.target.value }))
              }
            />
            <TextField
              label='Description'
              fullWidth
              size='small'
              multiline
              rows={2}
              value={notifParams.body}
              onChange={(e) =>
                setNotifParams((p) => ({ ...p, body: e.target.value }))
              }
            />
            <TextField
              label='Deep link'
              fullWidth
              size='small'
              value={notifParams.deepLink}
              onChange={(e) =>
                setNotifParams((p) => ({ ...p, deepLink: e.target.value }))
              }
              placeholder='/organiser'
            />
            <TextField
              label='Delay (seconds)'
              type='number'
              fullWidth
              size='small'
              value={notifParams.delay}
              onChange={(e) =>
                setNotifParams((p) => ({ ...p, delay: Number(e.target.value) }))
              }
              slotProps={{ htmlInput: { min: 0, max: 300 } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNotifModal(false)}>Cancel</Button>
          <Button variant='contained' onClick={handleSendNotification}>
            Send
          </Button>
        </DialogActions>
      </Dialog>
      {buildHash && (
        <CommitLink
          href={`https://github.com/KareHero/karehero-app/commit/${buildHash}`}
          target='_blank'
          rel='noreferrer'
        >
          {buildHash.slice(0, 7)}
        </CommitLink>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div<{ $environment?: string }>`
  position: relative;
  font: ${(props) => props.theme.typography.body.xs.regular};
  padding: ${(props) => props.theme.spacing[4]};
  text-align: center;

  ${(props) => {
    switch (props.$environment) {
      case 'local':
      case 'Local':
        return css`
          background: ${props.theme.color.success[50]};
          color: ${props.theme.color.neutral[10]};
        `;
      case 'dev':
      case 'Development':
        return css`
          background: ${props.theme.color.info[40]};
          color: ${props.theme.color.neutral[10]};
        `;
      case 'staging':
      case 'Staging':
        return css`
          background: ${props.theme.color.warning[50]};
          color: ${props.theme.color.neutral[10]};
        `;
      default:
        return css`
          background: ${props.theme.color.neutral[10]};
          color: ${props.theme.color.neutral[100]};
        `;
    }
  }}

  ${(props) =>
    props.theme.breakpoint.sm(css`
      font: ${props.theme.typography.body.md.regular};
      padding: ${props.theme.spacing[8]};
    `)}
`;

const SignOutButton = styled.button`
  all: unset;

  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  cursor: pointer;
  color: ${(props) => props.theme.color.neutral[10]};
  font: ${(props) => props.theme.typography.body.xxs.regular};
  padding-left: ${(props) => props.theme.spacing[4]};
  &:hover {
    text-decoration: underline;
  }

  ${(props) =>
    props.theme.breakpoint.sm(css`
      font: ${props.theme.typography.body.xs.regular};
      padding-left: ${props.theme.spacing[8]};
    `)}
`;

const TestToastButton = styled.button`
  all: unset;

  position: absolute;
  top: 0;
  bottom: 0;
  left: 80px;
  cursor: pointer;
  color: ${(props) => props.theme.color.neutral[10]};
  font: ${(props) => props.theme.typography.body.xxs.regular};
  padding-left: ${(props) => props.theme.spacing[4]};
  &:hover {
    text-decoration: underline;
  }

  ${(props) =>
    props.theme.breakpoint.sm(css`
      font: ${props.theme.typography.body.xs.regular};
      left: 110px;
    `)}
`;

const TestNotificationButton = styled.button`
  all: unset;

  position: absolute;
  top: 0;
  bottom: 0;
  left: 170px;
  cursor: pointer;
  color: ${(props) => props.theme.color.neutral[10]};
  font: ${(props) => props.theme.typography.body.xxs.regular};
  padding-left: ${(props) => props.theme.spacing[4]};
  &:hover {
    text-decoration: underline;
  }

  ${(props) =>
    props.theme.breakpoint.sm(css`
      font: ${props.theme.typography.body.xs.regular};
      left: 230px;
    `)}
`;

const LinearLink = styled.a`
  color: ${(props) => props.theme.color.neutral[10]};
`;

const CommitLink = styled.a`
  display: flex;
  align-items: center;
  color: ${(props) => props.theme.color.neutral[10]};
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  cursor: pointer;
  color: ${(props) => props.theme.color.neutral[10]};
  font: ${(props) => props.theme.typography.body.xxs.regular};
  padding-right: ${(props) => props.theme.spacing[4]};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }

  ${(props) =>
    props.theme.breakpoint.sm(css`
      font: ${props.theme.typography.body.xs.regular};
      padding-right: ${props.theme.spacing[8]};
    `)}
`;

DevTools.displayName = 'DevTools';

export default DevTools;
